import { useState, FormEvent, useEffect } from 'react'
import { isEmail } from '../../utils/stringUtils'
import { PublicQuizAPI } from '../../apis/PublicQuizAPI'
import { qcls, qfonts } from './quizTheme'

interface Props {
  slug?: string
  onStart?: (attemptId: string, isResuming: boolean, assessmentName: string, questionCap: number | null) => void
  /** Called instead of onStart when no slug is provided — user picks the quiz next */
  onFormSubmit?: (name: string, email: string, role: string) => void
  initialName?: string
  initialEmail?: string
}

export default function RegisterPage({ slug, onStart, onFormSubmit, initialName, initialEmail }: Props) {
  const [firstName, setFirstName] = useState(initialName?.split(' ')[0] ?? process.env.REACT_APP_QUIZ_PREFILL_NAME?.split(' ')[0] ?? '')
  const [lastName, setLastName] = useState(initialName?.split(' ').slice(1).join(' ') ?? process.env.REACT_APP_QUIZ_PREFILL_NAME?.split(' ').slice(1).join(' ') ?? '')
  const [email, setEmail] = useState(initialEmail ?? process.env.REACT_APP_QUIZ_PREFILL_EMAIL ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false)
  const [assessmentName, setAssessmentName] = useState('Digital capability')
  const [heroText, setHeroText] = useState('')
  const [description, setDescription] = useState('')

  const [questionCap, setQuestionCap] = useState<number | null>(null)

  useEffect(() => {
    if (!slug) return
    PublicQuizAPI.fetchInfo(slug)
      .then((info) => {
        if (info.name) setAssessmentName(info.name)
        if (info.heroText) setHeroText(info.heroText)
        if (info.description) setDescription(info.description)
        setQuestionCap(info.questionCap ?? null)
      })
      .catch(() => {})
  }, [slug])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!firstName.trim()) return setError('Please enter your first name.')
    if (!lastName.trim()) return setError('Please enter your last name.')
    if (!isEmail(email.trim())) return setError('Please enter a valid email address.')
    if (!agreedToPrivacy) return setError('Please agree to the Privacy Policy to continue.')

    const fullName = `${firstName.trim()} ${lastName.trim()}`

    // No slug — just collect form data and let parent route to quiz selection
    if (!slug) {
      onFormSubmit?.(fullName, email.trim().toLowerCase(), '')
      return
    }

    try {
      setLoading(true)
      const result = await PublicQuizAPI.start({
        name: fullName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        role: undefined,
        assessmentSlug: slug,
      })
      onStart?.(result.attemptId, result.isResuming, assessmentName, questionCap)
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row md:h-screen md:overflow-hidden">
      {/* ── Left: brand panel ── */}
      <div
        className="md:w-5/12 md:self-stretch flex flex-col items-center px-10 pt-16 relative"
        style={{
          backgroundImage: "linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url('/images/hero-banner-naked.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Yellow top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-brand-yellow" />

        {/* Top spacer: pushes content down, shrinks to 0 when tight */}
        <div className="flex-1" style={{ maxHeight: '15vh' }} />

        {/* Logo */}
        <div className="flex items-center gap-3 mb-14">
          <div className="w-10 h-10 bg-brand-yellow rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-black font-black text-xl leading-none">D</span>
          </div>
          <span className="text-white/80 text-xs uppercase tracking-[0.2em] font-semibold">
            Digital Competence
          </span>
        </div>

        {/* Headline */}
        <div className="pb-10">
          <h1 className={`text-5xl font-black text-white leading-[1.1] mb-6 ${qfonts.heading}`}>
            {(heroText || assessmentName) && <>{heroText || assessmentName}<br /></>}
            <span className="text-brand-yellow">assessment</span>
          </h1>
          {description && (
            <p className={`text-white/70 text-sm leading-relaxed max-w-[22rem] mb-12 ${qfonts.body}`}>
              {description.split('\n').map((line, i, arr) => (
                <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
              ))}
            </p>
          )}

          <div className="space-y-4">
            {[
              'Adaptive — questions tailored to your level',
              'Takes 10–15 minutes to complete',
              'Receive a personalised report on completion',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 bg-brand-yellow rounded flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className={`text-white/90 text-sm ${qfonts.supporting}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Spacer bottom */}
        <div className="flex-1" style={{ maxHeight: '20vh' }} />

        {/* Footer — desktop only, natural flow */}
        <div className="flex w-full h-0 md:h-10 overflow-hidden items-center justify-center flex-shrink-0">
          <p className="text-white/50 text-xs">
            &copy; {new Date().getFullYear()} UNSW. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Mobile horizontal divider ── */}
      <div className="md:hidden h-2 bg-black flex-shrink-0" />

      {/* ── Right: form panel ── */}
      <div className="flex-1 flex items-center justify-center px-8 py-14 bg-white md:[border-left:8px_solid_black]">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h2 className="text-[32px] font-black text-black mb-2 font-quiz-heading">Get started</h2>
            <p className="text-gray-400 text-sm">
              Enter your details to begin
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                autoComplete="given-name"
                className={qcls.input}
              />
            </div>

            <div>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                autoComplete="family-name"
                className={qcls.input}
              />
            </div>

            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                className={qcls.input}
              />
            </div>



            {error && (
              <div className={qcls.errorBox}>
                <svg className="w-[22px] h-[22px] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                {error}
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToPrivacy}
                onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0 accent-brand-yellow cursor-pointer"
              />
              <span className="text-sm text-gray-700 leading-snug">
                I agree to the terms of{' '}
                <a
                  href="https://www.unsw.edu.au/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-black hover:text-gray-600"
                >
                  UNSW's Privacy Policy
                </a>
              </span>
            </label>

            <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 border-2 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-95 active:scale-[0.99] transition-all bg-brand-yellow"
              style={{ color: 'rgb(26, 30, 43)', borderColor: 'rgb(26, 30, 43)', fontFamily: 'RobotoMono, Arial, sans-serif', fontSize: 16, fontWeight: 700 }}
            >
              {loading ? (
                <>
                  <svg className={qcls.spinner} fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Starting…
                </>
              ) : (
                <>
                  {slug ? 'Begin assessment' : 'Choose assessment'}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
            </div>

            <p className="text-xs text-center text-gray-400">
              {/* Have an existing session? Enter the same email to resume. */}
            </p>
          </form>
        </div>
      </div>

      {/* Footer — mobile only */}
      <div className="md:hidden h-12 flex items-center justify-center bg-black">
        <p className="text-white/40 text-xs">
          &copy; {new Date().getFullYear()} UNSW. All rights reserved.
        </p>
      </div>
    </div>
  )
}
